import type { OverviewControlTopPattern } from './overviewTypes';
import {
  liveViewPatternAreaLabel,
  liveViewPatternDisplayName,
  parseZoneFromGroupKey,
  parseZoneFromPatternLabel,
} from '../liveView/liveViewPatternDisplay';
import { parsePatternReviewLinkParams } from './overviewCameraEvidence';

export type LinkedCriticalRiskPattern = {
  patternLabel: string;
  displayName: string;
  incidentType?: string;
  groupKey?: string;
  zoneLabel: string;
  controlStatusLabel: string;
  patternOwner: string;
  linkToPatternReview: string;
  openLinkedEventRecords: number;
  severityRank: number;
  /** @deprecated Use openLinkedEventRecords */
  recentEventRecordCount: number;
};

export type CameraPatternLinkContext = {
  linkedPatternCount: number;
  patterns: LinkedCriticalRiskPattern[];
  primary: LinkedCriticalRiskPattern | null;
};

const SEVERITY_RANK: Record<string, number> = {
  CRITICAL: 0,
  HIGH: 1,
  MEDIUM: 2,
  LOW: 3,
};

function severityRank(severity: string | undefined): number {
  return SEVERITY_RANK[String(severity || '').toUpperCase()] ?? 4;
}

function toLinkedPattern(pattern: OverviewControlTopPattern): LinkedCriticalRiskPattern {
  const zoneRaw =
    parseZoneFromGroupKey(pattern.groupKey) ||
    parseZoneFromPatternLabel(pattern.patternLabel) ||
    '';
  const displayName = liveViewPatternDisplayName(pattern.patternLabel, pattern.incidentType);
  const openLinkedEventRecords = Number(pattern.actionScopeCount ?? 0);

  return {
    patternLabel: pattern.patternLabel,
    displayName,
    incidentType: pattern.incidentType,
    groupKey: pattern.groupKey,
    zoneLabel: liveViewPatternAreaLabel(pattern.patternLabel, pattern.groupKey) || zoneRaw,
    controlStatusLabel: pattern.controlStatusLabel,
    patternOwner: pattern.patternOwner,
    linkToPatternReview: pattern.linkToPatternReview,
    openLinkedEventRecords,
    recentEventRecordCount: openLinkedEventRecords,
    severityRank: severityRank(pattern.severity),
  };
}

function sortLinkedPatterns(patterns: LinkedCriticalRiskPattern[]): LinkedCriticalRiskPattern[] {
  return [...patterns].sort((a, b) => {
    if (a.severityRank !== b.severityRank) return a.severityRank - b.severityRank;
    if (a.openLinkedEventRecords !== b.openLinkedEventRecords) {
      return b.openLinkedEventRecords - a.openLinkedEventRecords;
    }
    return a.displayName.localeCompare(b.displayName);
  });
}

/** Map camera_id → linked critical-risk pattern context for Overview and Live View. */
export function buildCameraPatternLinkContext(
  topPatterns: OverviewControlTopPattern[] | null | undefined,
): Record<string, CameraPatternLinkContext> {
  const byCamera: Record<string, { patterns: LinkedCriticalRiskPattern[] }> = {};

  for (const pattern of topPatterns || []) {
    const params = parsePatternReviewLinkParams(pattern.linkToPatternReview);
    const cameraId = String(params?.cameraId || pattern.cameraId || '').trim();
    if (!cameraId) continue;

    const linked = toLinkedPattern(pattern);
    const existing = byCamera[cameraId] || { patterns: [] };
    existing.patterns.push(linked);
    byCamera[cameraId] = existing;
  }

  const out: Record<string, CameraPatternLinkContext> = {};
  for (const [cameraId, bucket] of Object.entries(byCamera)) {
    const patterns = sortLinkedPatterns(bucket.patterns);
    out[cameraId] = {
      linkedPatternCount: patterns.length,
      patterns,
      primary: patterns[0] || null,
    };
  }

  return out;
}

export function lookupCameraPatternLink(
  context: Record<string, CameraPatternLinkContext>,
  cameraId: string,
): CameraPatternLinkContext | null {
  const id = String(cameraId || '').trim();
  if (!id) return null;
  return context[id] || null;
}

export function patternRowThumbnailCameraId(pattern: OverviewControlTopPattern): string | null {
  const params = parsePatternReviewLinkParams(pattern.linkToPatternReview);
  return params?.cameraId || null;
}
